import * as React from "react"
import { Menu, Transition } from "@headlessui/react"

import { cn } from "@/utils"

/** Root dropdown wrapper */
function DropdownMenu({ children }: { children: React.ReactNode }) {
  return <Menu as="div" className="relative inline-block text-left">{children}</Menu>
}

/** Trigger button for the dropdown */
const DropdownMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, ...props }, ref) => (
  <Menu.Button
    ref={ref}
    className={cn("inline-flex items-center", className)}
    {...props}
  >
    {children}
  </Menu.Button>
))
DropdownMenuTrigger.displayName = "DropdownMenuTrigger"

/** Dropdown content panel */
function DropdownMenuContent({
  children,
  className,
  align = "end",
}: {
  children: React.ReactNode
  className?: string
  align?: "start" | "end"
}) {
  return (
    <Transition
      as={React.Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <Menu.Items
        className={cn(
          "absolute z-50 mt-2 min-w-[8rem] overflow-hidden rounded-md border border-border bg-popover p-1 shadow-md",
          align === "end" ? "right-0" : "left-0",
          className
        )}
      >
        {children}
      </Menu.Items>
    </Transition>
  )
}

/** Individual menu item */
const DropdownMenuItem = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { danger?: boolean }
>(({ className, danger, children, ...props }, ref) => (
  <Menu.Item>
    {({ active }) => (
      <button
        ref={ref}
        className={cn(
          "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none",
          active && "bg-accent text-accent-foreground",
          danger && "text-destructive",
          className
        )}
        {...props}
      >
        {children}
      </button>
    )}
  </Menu.Item>
))
DropdownMenuItem.displayName = "DropdownMenuItem"

/** Separator between menu items */
function DropdownMenuSeparator({ className }: { className?: string }) {
  return <div className={cn("-mx-1 my-1 h-px bg-border", className)} />
}

/** Label/header for a group of items */
function DropdownMenuLabel({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn("px-2 py-1.5 text-xs font-semibold text-muted-foreground", className)}>
      {children}
    </div>
  )
}

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
}
